import { z } from "zod";
import { slugString } from "./slug.js";
import { WEIGHT_KG_MAX, WEIGHT_KG_MIN } from "../units.js";
import type { MaxBumpCandidate } from "../maxBumpCandidates.js";

// The wire form of a post-save MAX proposal (#182): the client computes the
// candidates with computeMaxBumpCandidates and POSTs them to
// /api/me/max-bumps, where the server checks each one against its own MAX
// and turns the survivors into bell rows. `satisfies` ties the schema to the
// existing MaxBumpCandidate type, so the two cannot drift apart.

const Kg = z.number().min(WEIGHT_KG_MIN).max(WEIGHT_KG_MAX);

export const MaxBumpCandidateWire = z.object({
  exerciseId: slugString(64),
  suggestion: z.object({
    suggestedKg: Kg.positive(),
    e1RM: z.number().nonnegative().max(WEIGHT_KG_MAX * 2),
    fromSet: z.object({ weightKg: Kg, reps: z.number().int().min(1).max(100) }),
    replacesKg: Kg.optional(),
  }),
  direction: z.enum(["raise", "lower"]).optional(),
  regression: z
    .object({
      currentMaxKg: Kg,
      weeksSinceMax: z.number().int().nonnegative(),
    })
    .optional(),
}) satisfies z.ZodType<MaxBumpCandidate>;

export const MaxBumpOffer = z
  .object({
    workoutId: z.string().min(1).max(128),
    // The session's start: an older offer replayed from an outbox must not
    // replace a newer session's row. UTC Z form (toISOString), no offset.
    // The server refuses a value more than one day ahead of its clock.
    sessionStartedAt: z.iso.datetime(),
    candidates: z.array(MaxBumpCandidateWire).min(1).max(20),
  })
  .refine(
    (o) => new Set(o.candidates.map((c) => c.exerciseId)).size === o.candidates.length,
    { message: "duplicate exerciseId", path: ["candidates"] },
  );
export type MaxBumpOffer = z.infer<typeof MaxBumpOffer>;

// The answers to one row. Unknown fields are refused. `workoutId` is the one
// the athlete saw: when a newer session has replaced the row in the meantime
// the server answers 409 `superseded` instead of applying a value nobody read.
export const MaxBumpAccept = z.strictObject({
  workoutId: z.string().min(1).max(128).optional(),
});
export type MaxBumpAccept = z.infer<typeof MaxBumpAccept>;

// `snooze` is the "Bad day" answer, valid only on a downward row.
export const MaxBumpReject = z.strictObject({
  workoutId: z.string().min(1).max(128).optional(),
  snooze: z.boolean().optional(),
});
export type MaxBumpReject = z.infer<typeof MaxBumpReject>;

/**
 * The offer a client POSTs after a save: the candidates the wire takes, at
 * most 20, and the session start in Z form. Null when nothing is left to
 * send. A candidate the schema refuses is dropped, so one bad value does not
 * cost the others their row.
 */
export const maxBumpOfferOf = (
  pending: { workoutId: string; candidates: readonly MaxBumpCandidate[] },
  sessionStartedAt: string,
): MaxBumpOffer | null => {
  const startedMs = Date.parse(sessionStartedAt);
  if (!Number.isFinite(startedMs)) return null;
  const candidates: z.infer<typeof MaxBumpCandidateWire>[] = [];
  for (const cand of pending.candidates) {
    const wire = MaxBumpCandidateWire.safeParse(cand);
    if (wire.success) candidates.push(wire.data);
  }
  const parsed = MaxBumpOffer.safeParse({
    workoutId: pending.workoutId,
    sessionStartedAt: new Date(startedMs).toISOString(),
    candidates: candidates.slice(0, 20),
  });
  return parsed.success ? parsed.data : null;
};
